/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn, AliasType } from '../../registry/Component.type';
import { defaultValue } from './SignaturePad';
import { isNotEmpty } from '../../utils/isEmpty';
import { ItemType } from 'componentregistry/registry/ComponentRegistry';

const SignaturePadManager: ComponentManagerFn<'SignaturePad'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'SignaturePad'>(platform, 'SignaturePad'),
    type: 'SignaturePad',
    name: 'Signature Pad',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnSubmit'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      title: {
        label: 'Title',
        type: 'string',
      },
      open: {
        label: 'Open',
        type: 'boolean',
      },
      alias: {
        label: 'Data Store Alias',
        type: 'DataSourceAliasChooser',
        category: 'Data',
        allowedTypes: ['CloudDataStore', 'ReadOnlyDataStore', 'DataStore'],
        required: true,
      },
      viewAttribute: {
        label: 'View Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        category: 'Data',
        required: true,
        getAttributeDataTypes: () => ['Textarea'],
      },
      saveOnSubmit: {
        label: 'Save on Submit',
        type: 'boolean',
        category: 'Data',
        helpText:
          'Uncheck if you want to save the data store in a different action',
      },
    },
    isPropertyVisible(prop, itemData): boolean {
      if (prop === 'viewAttribute') return isNotEmpty(itemData.alias);
      return true;
    },
    getDisplayText: (item) => '',
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV' ? { aliasProp: 'alias' } : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    icon: { iconName: 'file-signature' },
    description: `**Overview**<br/>
The Signature Pad is a modal data capture component that allows users to draw a signature using a mouse, stylus, or touch gesture.
Unlike a text field where users type input, the Signature Pad captures drawing strokes, converts them into a Base64 encoded image string, and saves the result directly to a Data Store attribute. This is essential for legal approvals, delivery confirmations, and compliance workflows.

**1. Integration & Binding**<br/>
Since the signature is treated as a specialized data type, it requires specific configuration to ensure the image is stored correctly.
* **Data Store Alias:** Select the Data Store that will receive the signature. (Requirement: This is mandatory).
* **View Attribute:** Select the attribute within that store where the signature string will be saved.
* **Data Type Requirement:** The target attribute must be of type **Textarea**. Standard text fields are too short to hold the Base64 image data generated by the pad.

**2. Interaction & Lifecycle**<br/>
The Signature Pad is not an inline form element; it is a modal experience triggered by user action.
* **Open:** A boolean property that controls visibility. (Best Practice: Leave this false by default. Use a Button or Icon component with an OnClick event to set this property to true).
* **Save On Submit:**
    * **Enabled (True):** When the user clicks "Submit" on the pad, the signature is written to the store, and the store immediately triggers a save() operation.
    * **Disabled (False):** The signature is written to the store's local state, but no save occurs. Use this if the signature is just one part of a larger form that the user will save later.

**3. Triggers**<br/>
To handle post-signature logic, the component supports a specific lifecycle trigger.
* **TriggerOnSubmit:** Use this child component to execute workflows immediately after the user confirms their signature. (Example: Close the modal, show a "Thank You" message, or navigate to the next step).

**4. Usage Strategy**<br/>
* **Not for File Uploads:** This component generates a string representation of an image. If you need users to upload an existing file (like a scanned PDF), use the File Upload component instead.
* **Mobile Friendly:** The canvas automatically supports touch events, making it ideal for field applications (e.g., delivery drivers on tablets).`,
    category: 'Data',
    canHaveChildren: () => false,
    noOverlay: true,
  };
};

export default SignaturePadManager;
